import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Tag, Space, Select, Input, Modal, notification, Spin, Divider, Avatar,
} from 'antd';
import {
  ClockCircleOutlined, LoginOutlined, LogoutOutlined,
  CoffeeOutlined, CheckCircleOutlined, CloseOutlined,
  ThunderboltOutlined, UserOutlined, CalendarOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import styled, { keyframes, css } from 'styled-components';
import { hasPermission } from '../permissions';
import { getCurrentStaffId } from '../../authHelper';
import {
  getCurrentStatus, clockIn, clockOut,
  startBreak, endBreak, getBreakTypes, getTodaySummary,
} from '../../pages/sn_work_tracking/workTrackingService';

const { Option } = Select;
const { TextArea } = Input;

// ── animations ───────────────────────────────────────────────
const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(82,196,26,0.55); }
  70%  { box-shadow: 0 0 0 10px rgba(82,196,26,0); }
  100% { box-shadow: 0 0 0 0 rgba(82,196,26,0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── styled components ─────────────────────────────────────────
const Fab = styled.div`
  position: fixed;
  bottom: 18px;
  left: 18px;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 7px;
  background: rgba(255,255,255,0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1.5px solid ${p => p.$color || '#d9d9d9'};
  border-radius: 30px;
  padding: 5px 12px 5px 8px;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(0,0,0,0.10);
  transition: box-shadow 0.2s, transform 0.2s;
  user-select: none;

  &:hover {
    box-shadow: 0 4px 18px rgba(0,0,0,0.15);
    transform: translateY(-1px);
    background: rgba(255,255,255,0.90);
  }

  ${p => p.$active && css`animation: ${pulse} 2.2s infinite;`}
`;

const StatusDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
  box-shadow: 0 0 0 2px ${p => p.$color}33;
`;

const Panel = styled.div`
  position: fixed;
  bottom: 62px;
  left: 18px;
  z-index: 998;
  width: 248px;
  background: rgba(255,255,255,0.82);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 14px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.13);
  border: 1px solid rgba(255,255,255,0.6);
  overflow: hidden;
  animation: ${slideUp} 0.18s ease;
`;

const PanelHeader = styled.div`
  background: linear-gradient(135deg, ${p => p.$color}cc 0%, ${p => p.$colorLight}cc 100%);
  padding: 10px 12px 10px;
  color: #fff;
`;

const PanelBody = styled.div`
  padding: 10px 12px;
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0,0,0,0.05);

  &:last-child { border-bottom: none; }
`;

const StatLabel = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StatValue = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #1a1a2e;
`;

// ── status config ─────────────────────────────────────────────
const STATUS_CFG = {
  active:      { color: '#52c41a', colorLight: '#73d13d', label: 'Active',      tagColor: 'success' },
  on_break:    { color: '#faad14', colorLight: '#ffc53d', label: 'On Break',    tagColor: 'warning' },
  idle:        { color: '#fa8c16', colorLight: '#ffa940', label: 'Idle',        tagColor: 'orange'  },
  clocked_out: { color: '#bfbfbf', colorLight: '#d9d9d9', label: 'Clocked Out', tagColor: 'default' },
};

const fmt = (ds) => {
  if (!ds) return '--:--';
  try {
    return new Date(ds).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return '--:--'; }
};

// ── main export ───────────────────────────────────────────────
export default function WorkStatusWidget() {
  const staffId = getCurrentStaffId();
  if (!staffId) return null;
  return <WidgetInner staffId={staffId} />;
}

function WidgetInner({ staffId }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState(null);
  const [summary, setSummary] = useState(null);
  const [elapsed, setElapsed] = useState('0h 0m');

  // Break modal
  const [breakModal, setBreakModal] = useState(false);
  const [breakTypes, setBreakTypes] = useState({});
  const [selBreak, setSelBreak]     = useState('');
  const [breakNote, setBreakNote]   = useState('');

  const staffName = `${localStorage.getItem('firstName') || ''} ${localStorage.getItem('lastName') || ''}`.trim() || 'Staff';

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [stRes, smRes] = await Promise.allSettled([
        getCurrentStatus(staffId),
        getTodaySummary(staffId),
      ]);
      const st = stRes.status === 'fulfilled' ? stRes.value?.data : null;
      const sm = smRes.status === 'fulfilled' ? smRes.value?.data : null;
      setStatus(st);
      setSummary(sm);
      return st;
    } catch { }
    finally { setLoading(false); }
  }, [staffId]);

  // Auto clock-in: if today hasn't been auto-clocked, do it silently
  const autoClockIn = useCallback(async () => {
    const todayKey = `auto_clockin_${staffId}_${new Date().toDateString()}`;
    if (localStorage.getItem(todayKey)) return; // already done today
    try {
      await clockIn(staffId);
      localStorage.setItem(todayKey, '1');
    } catch {
      // Already clocked in or error — mark done to stop retrying
      localStorage.setItem(todayKey, '1');
    }
  }, [staffId]);

  useEffect(() => {
    load().then(st => {
      // Auto clock-in if not yet clocked in today
      if (!st || st.current_status === 'clocked_out') {
        autoClockIn().then(() => load(true));
      }
    });
    getBreakTypes().then(r => setBreakTypes(r.data || r)).catch(() => {});
    const iv = setInterval(() => load(true), 60000);
    return () => clearInterval(iv);
  }, [load, autoClockIn]);

  // Live elapsed counter — only count time from TODAY's session
  useEffect(() => {
    const tick = () => {
      if (!status?.clock_in_time || status?.current_status === 'clocked_out') {
        setElapsed('0h 0m');
        return;
      }
      const clockIn = new Date(status.clock_in_time);
      const now     = new Date();
      // If clock_in is from a previous day, don't count stale time
      const isToday = clockIn.toDateString() === now.toDateString();
      if (!isToday) { setElapsed('0h 0m'); return; }
      const mins = Math.max(0, Math.floor((now - clockIn) / 60000));
      setElapsed(`${Math.floor(mins / 60)}h ${mins % 60}m`);
    };
    tick();
    const iv = setInterval(tick, 30000);
    return () => clearInterval(iv);
  }, [status]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('[data-wid]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const cfg          = STATUS_CFG[status?.current_status] || STATUS_CFG.clocked_out;
  const isClockedOut = status?.current_status === 'clocked_out';
  const isOnBreak    = status?.current_status === 'on_break';

  // ── handlers ─────────────────────────────────────────────
  const act = async (fn, successMsg) => {
    setLoading(true);
    try {
      const r = await fn();
      notification.success({ message: successMsg(r), placement: 'bottomLeft' });
      await load();
    } catch (e) {
      notification.error({ message: e.message || 'Failed', placement: 'bottomLeft' });
      setLoading(false);
    }
  };

  const handleClockIn  = () => act(() => clockIn(staffId),  () => 'Clocked in!');
  const handleEndBreak = () => act(() => endBreak(staffId), r  => `Break ended — ${r.data.break_duration_formatted}`);

  const handleClockOut = () => {
    setOpen(false);
    Modal.confirm({
      title: 'Clock Out',
      content: 'Are you sure you want to clock out?',
      okType: 'danger',
      onOk: () => act(() => clockOut(staffId), r => `Clocked out — ${r.data.total_work_time} worked`),
    });
  };

  const handleStartBreak = async () => {
    if (!selBreak) { notification.warning({ message: 'Select break type', placement: 'bottomLeft' }); return; }
    setLoading(true);
    try {
      await startBreak(staffId, selBreak, breakNote);
      notification.success({ message: 'Break started', placement: 'bottomLeft' });
      setBreakModal(false); setSelBreak(''); setBreakNote('');
      await load();
    } catch (e) {
      notification.error({ message: e.message || 'Failed', placement: 'bottomLeft' });
      setLoading(false);
    }
  };

  // ── data helpers ─────────────────────────────────────────
  const activities   = summary?.activity_summary?.total_activities || 0;
  const breakTime    = summary?.work_status?.total_break_formatted || `${summary?.breaks?.total_minutes || 0}m`;
  const productive   = summary?.work_status?.productive_formatted  || '—';
  const lastActivity = summary?.timeline?.[0];
  const breakCount   = summary?.breaks?.count || 0;

  return (
    <>
      {/* ── Floating button ── */}
      <Fab
        data-wid="widget"
        $color={cfg.color}
        $active={status?.current_status === 'active'}
        onClick={() => setOpen(o => !o)}
      >
        <StatusDot $color={cfg.color} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap' }}>
            {isClockedOut ? 'Not Clocked In' : elapsed}
          </span>
          <span style={{ fontSize: 10, color: '#8c8c8c', fontWeight: 500 }}>{cfg.label}</span>
        </div>
      </Fab>

      {/* ── Expanded panel ── */}
      {open && (
        <Panel data-wid="widget">
          {/* Header */}
          <PanelHeader $color={cfg.color} $colorLight={cfg.colorLight}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar size={28} icon={<UserOutlined />} style={{ background: 'rgba(255,255,255,0.3)', color: '#fff', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, lineHeight: 1.3 }}>{staffName}</div>
                  <Tag
                    color="rgba(255,255,255,0.25)"
                    style={{ color: '#fff', border: '1px solid rgba(255,255,255,0.4)', marginTop: 2, fontSize: 10, borderRadius: 20, padding: '0 6px', lineHeight: '16px' }}
                  >
                    {cfg.label}
                  </Tag>
                </div>
              </div>
              <CloseOutlined
                onClick={() => setOpen(false)}
                style={{ color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 13, marginTop: 2 }}
              />
            </div>

            {/* Big time display */}
            {!isClockedOut && (
              <div style={{ marginTop: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 10, opacity: 0.8, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                  Time Worked Today
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.5 }}>{elapsed}</div>
                <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>
                  <CalendarOutlined style={{ marginRight: 3 }} />
                  Clocked in at {fmt(status?.clock_in_time)}
                </div>
              </div>
            )}
          </PanelHeader>

          {/* Body */}
          <PanelBody>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '12px 0' }}><Spin /></div>
            ) : (
              <>
                {/* Stats */}
                <StatRow>
                  <StatLabel><CoffeeOutlined />Break Time</StatLabel>
                  <StatValue>{breakTime} ({breakCount} breaks)</StatValue>
                </StatRow>
                <StatRow>
                  <StatLabel><ThunderboltOutlined />Productive</StatLabel>
                  <StatValue>{productive}</StatValue>
                </StatRow>
                <StatRow>
                  <StatLabel><RiseOutlined />Activities</StatLabel>
                  <StatValue>{activities} entries today</StatValue>
                </StatRow>

                {/* Last activity */}
                {lastActivity && (
                  <StatRow>
                    <StatLabel><ClockCircleOutlined />Last Activity</StatLabel>
                    <StatValue style={{ maxWidth: 160, textAlign: 'right', fontSize: 12 }}>
                      <Tag color="blue" style={{ fontSize: 11 }}>{lastActivity.activity_category}</Tag>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                        {fmt(lastActivity.start_time)}
                      </div>
                    </StatValue>
                  </StatRow>
                )}

                <Divider style={{ margin: '8px 0' }} />

                {/* Action buttons */}
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                  {isClockedOut ? (
                    <Button type="primary" size="small" icon={<LoginOutlined />} block onClick={handleClockIn}
                      style={{ background: '#52c41a', borderColor: '#52c41a', borderRadius: 7, fontSize: 12 }}>
                      Clock In
                    </Button>
                  ) : (
                    <>
                      {isOnBreak ? (
                        <Button type="primary" size="small" icon={<CheckCircleOutlined />} block onClick={handleEndBreak}
                          style={{ borderRadius: 7, fontSize: 12 }}>
                          End Break
                        </Button>
                      ) : (
                        <Button size="small" icon={<CoffeeOutlined />} block onClick={() => { setOpen(false); setBreakModal(true); }}
                          style={{ borderRadius: 7, fontSize: 12 }}>
                          Take Break
                        </Button>
                      )}
                      <Button danger size="small" icon={<LogoutOutlined />} block onClick={handleClockOut}
                        style={{ borderRadius: 7, fontSize: 12 }}>
                        Clock Out
                      </Button>
                    </>
                  )}
                </Space>
              </>
            )}
          </PanelBody>
        </Panel>
      )}

      {/* Break modal */}
      <Modal
        title="Start Break"
        open={breakModal}
        onOk={handleStartBreak}
        onCancel={() => { setBreakModal(false); setSelBreak(''); setBreakNote(''); }}
        okText="Start Break"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label>Break Type *</label>
            <Select style={{ width: '100%', marginTop: 8 }} placeholder="Select type"
              value={selBreak} onChange={setSelBreak} size="large">
              {Object.entries(breakTypes).map(([k, v]) => <Option key={k} value={v}>{v}</Option>)}
            </Select>
          </div>
          <div>
            <label>Notes (Optional)</label>
            <TextArea rows={3} value={breakNote} onChange={e => setBreakNote(e.target.value)} style={{ marginTop: 8 }} />
          </div>
        </Space>
      </Modal>
    </>
  );
}
